// src/hooks/useWorkspaceGroups.js
import { useState, useEffect, useMemo } from 'react';
import { useGroupedJobs } from './useGroupedJobs';
import { useGroupedByWorkspace } from './useGroupedByWorkspace';
import { dedupeFetch } from '../../../apiRequestCache';

// Global cache for metadata (shared across all hook instances)
const metadataCache = new Map();

export const useWorkspaceGroups = (stackedJobs, userId, apiUrl) => {
  // 1. Group projects by workspace (without metadata)
  const { projects, ungroupedJobs } = useGroupedJobs(stackedJobs);
  const { workspaces: baseWorkspaces } = useGroupedByWorkspace(projects);

  // 2. State for enriched workspaces and loading status
  const [workspacesWithMeta, setWorkspacesWithMeta] = useState([]);
  const [loadingMetadata, setLoadingMetadata] = useState(false);
  const [metadataError, setMetadataError] = useState(null);

  // 3. Fetch workspace + project metadata from API
  useEffect(() => {
    if (!userId || !apiUrl) return;

    const fetchMetadata = async () => {
      setLoadingMetadata(true);
      setMetadataError(null);

      const wsCacheKey = `workspaceMetadata_${userId}`;
      const projCacheKey = `projectMetadata_${userId}`;

      try {
        // ── Workspace metadata ──────────────────────────────────────────────
        let metadataMap;
        if (metadataCache.has(wsCacheKey)) {
          metadataMap = metadataCache.get(wsCacheKey);
        } else {
          const data = await dedupeFetch(
            `${apiUrl}/api/SiteNote/GetStackedWorkspaces?pageNumber=1&pageSize=10000&userId=${userId}`
          );
          metadataMap = new Map();
          (data.workspaces || []).forEach(ws => {
            metadataMap.set(ws.workspaceId, ws);
          });
          metadataCache.set(wsCacheKey, metadataMap);
        }

        // ── Project metadata (for descriptions) ─────────────────────────────
        let projectMetadataMap;
        if (metadataCache.has(projCacheKey)) {
          projectMetadataMap = metadataCache.get(projCacheKey);
        } else {
          try {
            const projectData = await dedupeFetch(
              `${apiUrl}/api/SiteNote/GetStackedProjects?pageNumber=1&pageSize=10000&userId=${userId}`
            );
            projectMetadataMap = new Map();
            (projectData.projects || []).forEach(proj => {
              projectMetadataMap.set(proj.projectId, proj);
            });
            metadataCache.set(projCacheKey, projectMetadataMap);
          } catch {
            projectMetadataMap = new Map();
          }
        }

        enrichWorkspaces(metadataMap, projectMetadataMap);
      } catch (err) {
        console.error('Error fetching metadata:', err);
        setMetadataError(err);
        // Fallback: use base workspaces without extra metadata
        setWorkspacesWithMeta(baseWorkspaces);
      } finally {
        setLoadingMetadata(false);
      }
    };

    const enrichWorkspaces = (metadataMap, projectMetadataMap = new Map()) => {
      const enriched = baseWorkspaces.map(workspace => {
        const meta = metadataMap.get(workspace.workspaceId);

        // Enrich each project with its description + last-updated user from GetStackedProjects
        const enrichedProjects = (workspace.projects || []).map(project => {
          const projectMeta = projectMetadataMap.get(project.projectId);
          return {
            ...project,
            description: projectMeta?.projectDescription || null,
            lastSiteNoteUserName: projectMeta?.lastSiteNoteUserName || null,
          };
        });

        if (meta) {
          return {
            ...workspace,
            totalSiteNotes: meta.totalSiteNotes,
            latestTimeStamp: meta.latestTimeStamp,
            lastSiteNote: meta.lastSiteNote,
            lastSiteNoteUserName: meta.lastSiteNoteUserName,
            workspaceFriendlyId: meta.workspaceFriendlyId,
            description: meta.description || null,
            projects: enrichedProjects,
          };
        }
        return { ...workspace, projects: enrichedProjects };
      });
      setWorkspacesWithMeta(enriched);
    };

    fetchMetadata();
  }, [apiUrl, userId, baseWorkspaces]);

  // 4. Return enriched workspaces + ungrouped jobs + loading/error states
  return {
    workspaces: workspacesWithMeta,
    ungroupedJobs,
    loadingMetadata,
    metadataError,
  };
};
